import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CardActionArea,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { InfiniteHitsProvided } from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  InstantSearch,
} from 'react-instantsearch-dom'
import { IconButton, SkeletonText } from 'utoniq-ui'

import { FollowArtistsFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import {
  HorizontalList,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import {
  Cell,
  CellFrame,
  Organism,
  OrganismFrame,
  Template,
} from '@/core/architecture'

import * as algolia from '../../../../lib/algolia'

const client = algolia.client()

export * from './fragments'

export type Artist = {
  id: string
  displayName: string
  accountId: string
  formattedAccountId: string
  avatar: {
    uri: string
    mediumUri: string
  }
}
export type State = {
  loading: boolean
  artists: Artist[]
  followees: Maybe<FollowArtistsFragment>
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
}

export type Action = {}

export const GlobalArtistTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('globalArtist')
  const { routerPages, routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  return (
    <BaseLayout
      navigation={{
        type: 'global',
        props: {
          hasManagementTask: !!state.user?.hasManagementTask,
          unreadNotificationCount: state.user?.unreadNotificationCount,
          hasSettingTask: !!state.user?.isMembershipPersonalInformationRequired,
        },
      }}
      header={{
        title: t('title'),
        left: config.applicationType === 'organization' && (
          <IconButton.AvatarNoImage
            edge="start"
            onClick={() => setOpenSideMenu(!openSideMenu)}
            color="icon.line"
            bgcolor="icon.background"
            badge={
              state.user?.hasManagementTask ||
              !!state.user?.unreadNotificationCount ||
              !!state.user?.isMembershipPersonalInformationRequired
            }
          />
        ),
        right: (
          <IconButton.Search
            edge="end"
            href={routerPages.ArtistSearch}
            bgcolor="transparent"
            sx={{ color: 'grey.600' }}
          />
        ),
      }}
    >
      <Head title={routerNames.GlobalArtist} />
      <SideMenu
        followArtists={state.followArtists}
        memberArtists={state.memberArtists}
        open={openSideMenu}
        onClose={() => setOpenSideMenu(false)}
        user={state.user}
        isArtist={state.isArtist}
      />
      {state.user && (
        <FollowingCell followees={state.followees} loading={state.loading} />
      )}
      <InstantSearch
        indexName={algolia.indexes.organizationArtists}
        searchClient={client}
      >
        <Configure filters={algolia.filters.organizationId} />
        <ArtistsCellWithAlgolia loading={state.loading} />
      </InstantSearch>
    </BaseLayout>
  )
}

type FollowingCellProps = Pick<State, 'followees' | 'loading'>
const FollowingCell: Cell<FollowingCellProps> = ({ followees, loading }) => {
  const { t } = useTranslation('globalArtist')
  const { routerPages, generatePath } = useRouter()
  return (
    <CellFrame>
      <HorizontalList
        title={t('following')}
        titleTypographyProps={{ variant: 'h3' }}
        sx={{ pb: 2 }}
        spacing={4}
        items={followees?.edges ?? []}
        renderItem={({ node }) => (
          <ButtonBase
            key={node.artist.id}
            href={generatePath(routerPages.ArtistTop, {
              artistId: node.artist.id,
            })}
          >
            <Avatar
              src={node.artist.avatar?.smallUri ?? ''}
              alt={node.artist.displayName ?? ''}
              sx={{ width: 64, height: 64 }}
            />
          </ButtonBase>
        )}
        loading={loading}
        loadingItemLength={5}
        LoadingComponent={() => (
          <Skeleton variant="circular" width={64} height={64} />
        )}
      />
    </CellFrame>
  )
}

/**
 * @todo
 * [UT-3167] Algoliaの検索ロジック切り分け追加時に外す
 * https://app.clickup.com/t/908127/UT-3167
 */
const ArtistsCellWithAlgolia = connectInfiniteHits<
  InfiniteHitsProvided & { loading: boolean },
  {}
>(({ hits, hasMore, refineNext, loading }) => {
  if (loading) {
    return <ArtistsLoadingCell />
  }
  /**
   * @description
   * algoliaへの依存を避けるために、objectID→idへ変数名を変更
   */
  const artists = hits.map(artist => ({ ...artist, id: artist.objectID }))
  return (
    <ArtistsCell
      hasNextPage={hasMore}
      onFetchMore={async () => refineNext()}
      artists={artists}
    />
  )
})

const ArtistsCell: Cell<{
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
  artists: Artist[]
}> = ({ artists, onFetchMore, hasNextPage }) => {
  const { t } = useTranslation('globalArtist')

  return (
    <CellFrame sx={{ px: 5 }}>
      <Typography variant="h3" sx={{ pb: 4 }}>
        {t('artist')}
      </Typography>
      <ScrollFetcher
        loading={false}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid}
        itemComponentProps={{ item: true, xs: 6, md: 4 }}
        emptyMessage={<Box />}
        nodes={artists?.map(artist => (
          <ArtistCard artist={artist} />
        ))}
      />
    </CellFrame>
  )
}

const ArtistsLoadingCell: Cell = () => {
  const { t } = useTranslation('globalArtist')

  return (
    <CellFrame sx={{ px: 5 }}>
      <Typography variant="h3" sx={{ pb: 4 }}>
        {t('artist')}
      </Typography>
      <Grid container spacing={5}>
        {Array.from(Array(6).keys()).map(key => (
          <Grid item xs={6} md={4} key={key}>
            <Stack spacing={4} justifyContent="center" alignItems="center">
              <Skeleton variant="circular" width={158} height={158} />
              <SkeletonText variant="small" width="50%" />
            </Stack>
          </Grid>
        ))}
      </Grid>
    </CellFrame>
  )
}

const ArtistCard: Organism<{ artist: Artist }> = ({ artist }) => {
  const { routerPages, generatePath } = useRouter()
  return (
    <OrganismFrame sx={{ height: '100%' }}>
      <Card
        sx={{
          display: 'grid',
          gap: 3,
          height: '100%',
          boxShadow: 0,
        }}
      >
        <CardActionArea
          href={generatePath(routerPages.ArtistTop, {
            artistId: artist.id,
          })}
          sx={{ bgcolor: 'background.default' }}
        >
          <Avatar
            src={artist?.avatar?.mediumUri ?? artist?.avatar?.uri ?? ''}
            alt={artist?.displayName ?? ''}
            sx={{ width: 158, height: 158, mx: 'auto' }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {artist?.displayName ?? ''}
          </Typography>
        </CardActionArea>
      </Card>
    </OrganismFrame>
  )
}
